import Avo from 'analytics/Avo';
import type { ServiceLine } from 'api/types/service.types';
import type { TimeSlots } from 'api/types/timeSlot.types';
import { useConfig } from 'context/app-config';
import {
  selectCurrentOrder,
  selectisPremiumTimeSlot,
  selectIsUrgentOrder,
  selectServiceLineCollectionMethods,
  selectServiceLineDropOffs,
} from 'pages/newOrder/newOrder.selectors';
import {
  resetServiceLinesDropoffs,
  updateDropoffTimeForServiceLine,
  updateIsDropoffAtDoor,
  updateIsDropoffAtDoorForServiceLine,
  updateNewOrder,
} from 'pages/newOrder/newOrder.slice';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import { selectActiveOrdersCount, selectCompletedOrdersCount } from 'redux-stores/slices/authSlice';

import type { SelectedSlots } from './useTimeSlotSelector';

interface IPickupAndDropoffHookProps {
  action: 'PICKUP' | 'DROPOFF';
  serviceLine?: ServiceLine;
  serviceLineDependancy?: ServiceLine;
  isLoading?: boolean;
}

function usePickupAndDropoffDefault({ action, serviceLine, serviceLineDependancy }: IPickupAndDropoffHookProps) {
  const dispatch = useDispatch();
  const config = useConfig();
  const activeOrdersCount = useSelector(selectActiveOrdersCount);
  const completedOrdersCount = useSelector(selectCompletedOrdersCount);
  const isPremiumTimeSlot = useSelector((state: RootState) => selectisPremiumTimeSlot({ ...state, serviceLine }));
  const isUrgent = useSelector((state: RootState) => selectIsUrgentOrder({ ...state, serviceLine }));
  const {
    isPickupAtDoor,
    isDropoffAtDoor: defaultIsDropoffAtDoor,
    pickupTimeslot,
    dropoffTimeslot: defaultDropoffTimeslot,
    addressId,
  } = useSelector(selectCurrentOrder);

  const serviceLineDropoffs = useSelector(selectServiceLineDropOffs);
  const serviceLineCollectionMethods = useSelector(selectServiceLineCollectionMethods);

  const dropoffTimeslot = useMemo(
    () =>
      config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs && serviceLine ? serviceLineDropoffs[serviceLine] : defaultDropoffTimeslot,
    [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, defaultDropoffTimeslot, serviceLine, serviceLineDropoffs]
  );

  const isDropOffDisabled = useMemo(() => {
    if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs) {
      return (action === 'DROPOFF' && !pickupTimeslot) || (serviceLineDependancy && !serviceLineDropoffs[serviceLineDependancy]);
    }
    return action === 'DROPOFF' && !pickupTimeslot;
  }, [action, config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, pickupTimeslot, serviceLineDependancy, serviceLineDropoffs]);

  const isDisabled = (action === 'PICKUP' && !addressId) || isDropOffDisabled;
  const selectedTimeslot = action === 'PICKUP' ? pickupTimeslot : dropoffTimeslot;

  const { date: pickupDate, slotId: pickupSlotId } = pickupTimeslot || {};
  const { date: dropoffDate, slotId: dropoffSlotId } = dropoffTimeslot || {};

  const preSelectedSlots: SelectedSlots = {
    dateSlotId: action === 'PICKUP' ? pickupDate || '' : dropoffDate || '',
    timeSlotId: action === 'PICKUP' ? pickupSlotId || '' : dropoffSlotId || '',
  };

  const isDropoffAtDoor = useMemo(
    () =>
      config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs && serviceLine
        ? Boolean(serviceLineCollectionMethods[serviceLine]?.isDropoffAtDoor)
        : defaultIsDropoffAtDoor,
    [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, defaultIsDropoffAtDoor, serviceLine, serviceLineCollectionMethods]
  );

  const isCollectionMethodAtDoor = action === 'PICKUP' ? isPickupAtDoor : isDropoffAtDoor;

  const handleTimeslotSelected = useCallback(
    (selectedAction: string, timeslot: TimeSlots.Timeslot) => {
      if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs && selectedAction === 'DROPOFF') {
        dispatch(updateDropoffTimeForServiceLine({ ...timeslot, serviceLine: serviceLine!! }));
      } else {
        dispatch(
          updateNewOrder({
            ...(selectedAction === 'PICKUP' && { pickupTimeslot: timeslot }),
            ...(selectedAction === 'DROPOFF' && { dropoffTimeslot: timeslot }),
          })
        );

        if (selectedAction === 'PICKUP') {
          const shouldResetDropoff = timeslot.date !== pickupDate || timeslot.slotId !== pickupSlotId;

          if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs) {
            if (shouldResetDropoff) {
              // if pickup changed then must reset dropoff for all service lines.
              dispatch(resetServiceLinesDropoffs());
            }
          } else if (shouldResetDropoff) {
            dispatch(updateNewOrder({ dropoffTimeslot: undefined }));
          }
        }
      }
    },
    [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, dispatch, pickupDate, pickupSlotId, serviceLine]
  );

  const handleCollectionMethodSelected = useCallback(
    async (selectedAction: string, collectionMethod: string) => {
      if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs && selectedAction === 'DROPOFF') {
        dispatch(
          updateIsDropoffAtDoorForServiceLine({
            isDropoffAtDoor: collectionMethod === 'AT_DOOR',
            serviceLine: serviceLine!!,
          })
        );
      } else if (selectedAction === 'DROPOFF') {
        const isDropoffAtDoorCheck = collectionMethod === 'AT_DOOR';
        if (isDropoffAtDoor !== isDropoffAtDoorCheck) {
          dispatch(updateIsDropoffAtDoor(isDropoffAtDoorCheck));
        }
      } else {
        dispatch(updateNewOrder({ isPickupAtDoor: collectionMethod === 'AT_DOOR' }));
      }
    },
    [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, dispatch, isDropoffAtDoor, serviceLine]
  );

  const handleTimeslotSelectorSubmit = useCallback(
    (pickedTimeslot: TimeSlots.Timeslot) => {
      if (pickedTimeslot) {
        handleTimeslotSelected(action, pickedTimeslot);
        if (action === 'PICKUP') {
          Avo.schedulePickup({
            pickupDay: pickedTimeslot.date || '',
            pickupTime: pickedTimeslot.selectedDateTimeText,
          });
        } else {
          Avo.scheduleDropoff({
            dropoffDay: pickedTimeslot.date || '',
            dropoffTime: pickedTimeslot.selectedDateTimeText,
          });
        }
      }
    },
    [action, handleTimeslotSelected]
  );

  return {
    activeOrdersCount,
    completedOrdersCount,
    isPremiumTimeSlot,
    isUrgent,
    isDisabled,
    selectedTimeslot,
    isCollectionMethodAtDoor,
    handleCollectionMethodSelected,
    handleTimeslotSelectorSubmit,
    preSelectedSlots,
  };
}

export const usePickupAndDropoff = usePickupAndDropoffDefault;
